import { useCallback } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { SenderSection } from '~/components/forms/shared/fields/SenderSection'
import { ReverseSwapSuggestionMessage } from '~/components/forms/swap/fields/ReverseSwapSuggestionMessage'
import { SwapPriceImpactMessage } from '~/components/forms/swap/fields/SwapPriceImpactMessage'
import { useEarnTokenEnforcement } from '~/components/forms/transfer/hooks/useEarnTokenEnforcement'
import { useIsBelowMinimumOfframpAmount } from '~/components/forms/transfer/hooks/useIsBelowMinimumOfframpAmount'
import { useMemoPrefill } from '~/components/forms/transfer/hooks/useMemoPrefill'
import { useSendRuleEnforcement } from '~/components/forms/transfer/hooks/useSendRuleEnforcement'
import {
  useIsInternalTransfer,
  useTokenAndChainFilter,
} from '~/components/forms/transfer/hooks/useTransferForm'
import { MultiRecipientSection } from '~/components/forms/transfer/multi/MultiRecipientSection'
import { SingleRecipientSection } from '~/components/forms/transfer/single/SingleRecipientSection'
import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { SupportedChainId } from '~/constants/chains'
import { usePayeeLookupByAddress } from '~/hooks/compliance/usePayeeLookup'
import { IAddress } from '~/types'

export const TransferFormSection = ({
  defaultSender,
  defaultRecipient,
  isOfframpVariant,
  lockRecipients,
  isMultiRecipient,
  isPrivate,
  forceAllowSenderSelection,
  isLoadingQuote,
}: {
  defaultSender?: IAddress
  defaultRecipient?: IAddress
  isOfframpVariant?: boolean
  lockRecipients?: boolean
  isMultiRecipient?: boolean
  isPrivate?: boolean
  forceAllowSenderSelection?: boolean
  isLoadingQuote?: boolean
}) => {
  const methods = useFormContext<TransferFormValues>()
  const { control } = methods

  useIsBelowMinimumOfframpAmount({ methods })

  const recipients = useWatch({ control, name: 'recipients' })
  const sender = useWatch({ control, name: 'sender' })

  const isCompliantPayment =
    !!recipients[0]?.receiver?.compliantPaymentRelationId
  const receiver = recipients[0]?.receiver?.address

  const { payee } = usePayeeLookupByAddress(receiver)

  const isInternalTransfer = useIsInternalTransfer({
    recipientAddress: receiver,
  })

  const prefillMemo = useMemoPrefill({
    isMultiRecipient: !!isMultiRecipient,
  })

  const { tokenFilter: baseTokenFilter, chainFilter: baseChainFilter } =
    useTokenAndChainFilter({
      isCompliantPayment,
      payee,
      isInternalTransfer,
      isPrivate,
      isMultiRecipient: Boolean(recipients.length > 1) && isMultiRecipient,
    })

  const { sendRuleTokenFilter, sendRuleChainFilter, hasSendRuleRestrictions } =
    useSendRuleEnforcement()

  useEarnTokenEnforcement()

  const tokenFilter = useCallback(
    (token: { chainId: SupportedChainId; address: string }) =>
      baseTokenFilter(token) && sendRuleTokenFilter(token),
    [baseTokenFilter, sendRuleTokenFilter],
  )

  const chainFilter = useCallback(
    (chainId: SupportedChainId) =>
      baseChainFilter(chainId) && sendRuleChainFilter(chainId),
    [baseChainFilter, sendRuleChainFilter],
  )

  return (
    <div className="space-y-6">
      {(!defaultSender || forceAllowSenderSelection) && (
        <SenderSection
          control={control}
          senderInputName="sender"
          title="From"
          hideBalance={true}
          onSelect={(value) => prefillMemo({ senderAddress: value })}
        />
      )}
      {recipients.length > 1 || isMultiRecipient ? (
        <MultiRecipientSection
          sender={sender}
          tokenFilter={tokenFilter}
          chainFilter={chainFilter}
          hasSendRuleRestrictions={hasSendRuleRestrictions}
        />
      ) : (
        <SingleRecipientSection
          sender={sender}
          isOfframpVariant={isOfframpVariant}
          defaultRecipient={defaultRecipient}
          lockRecipient={lockRecipients ?? false}
          isPrivate={!!isPrivate}
          tokenFilter={tokenFilter}
          chainFilter={chainFilter}
          hasSendRuleRestrictions={hasSendRuleRestrictions}
          onRecipientSelect={(value) =>
            prefillMemo({ recipientAddress: value || undefined })
          }
        />
      )}
      <ReverseSwapSuggestionMessage
        isLoadingQuote={isLoadingQuote}
        isPrivate={isPrivate}
      />
      <SwapPriceImpactMessage isLoadingQuote={isLoadingQuote} />
    </div>
  )
}
